import type { Affiliation, MeUser } from '@tomato/shared';
import {
  AFFILIATION_LABELS,
  AFFILIATIONS,
  checkNickname,
  NICKNAME_CHANGE_LIMIT,
  NICKNAME_ERROR_MESSAGES,
  NICKNAME_RULES,
  nicknameWidth,
  suggestNickname,
} from '@tomato/shared';
import { useEffect, useState } from 'react';

import { api, ApiError } from '../api/client';
import { Button, CenterCard, Checkbox, Label, Logo, Notice, Select, TextField } from '../components/ui';
import { clearStoredRef, getStoredRef } from '../lib/referral';
import { useSessionStore } from '../store/session';

interface AvailabilityResponse {
  available: boolean;
  message?: string;
}

interface OnboardingResponse {
  user: MeUser;
  onboarded: boolean;
}

export function OnboardingScreen() {
  const me = useSessionStore((s) => s.me);
  const completeOnboarding = useSessionStore((s) => s.completeOnboarding);
  const logout = useSessionStore((s) => s.logout);
  const [invited] = useState(() => getStoredRef() !== null);

  const [nickname, setNickname] = useState('');
  const [affiliation, setAffiliation] = useState<Affiliation | ''>('');
  const [visible, setVisible] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [availability, setAvailability] = useState<string>('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const check = checkNickname(nickname);
  const localError = nickname && !check.ok ? NICKNAME_ERROR_MESSAGES[check.error!] : '';

  // 형식이 맞을 때만 서버에 중복을 물어본다. 입력이 멈추면 조회(디바운스).
  useEffect(() => {
    setAvailability('');
    if (!check.ok) return;
    const t = setTimeout(() => {
      void api
        .get<AvailabilityResponse>(`/api/nickname/available?value=${encodeURIComponent(check.value)}`)
        .then((res) => setAvailability(res.available ? '' : (res.message ?? '')))
        .catch(() => {
          /* 중복 확인 실패는 조용히 넘긴다. 제출 시 서버가 최종 판정한다. */
        });
    }, 400);
    return () => clearTimeout(t);
  }, [check.ok, check.value]);

  // 계열사를 고르지 않으면 공개할 것도 없다.
  useEffect(() => {
    if (!affiliation) setVisible(false);
  }, [affiliation]);

  const canSubmit = check.ok && !availability && agreed && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post<OnboardingResponse>('/api/onboarding', {
        nickname: check.value,
        affiliation: affiliation || null,
        affiliationVisible: visible,
        agreeTerms: true,
        ref: getStoredRef(), // 초대 링크로 왔으면 추천인에게 100토마토
      });
      clearStoredRef();
      completeOnboarding(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  const width = nicknameWidth(check.value);

  return (
    <CenterCard>
      <Logo sub="처음 오셨네요. 이것만 정하면 끝이에요." />

      {invited && (
        <div className="mb-5 rounded-xl bg-tomato-50 px-3.5 py-2.5 text-[13px] text-tomato-700">
          🍅 초대로 오셨네요! 가입을 마치면 초대해 준 분께 100토마토가 적립돼요.
        </div>
      )}

      <div className="mb-5">
        <Label hint={`${width}/${NICKNAME_RULES.maxWidth}칸`}>활동명</Label>
        <TextField
          placeholder="다른 사람에게 보일 이름"
          value={nickname}
          disabled={busy}
          autoFocus
          onChange={(e) => setNickname(e.target.value)}
        />
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-xs text-slate-400">영문 5자 또는 한글 2자부터.</p>
          {/* 이름 짓기가 막막할 때 — 인디언식 이름을 랜덤으로 제안한다. */}
          <button
            type="button"
            disabled={busy}
            onClick={() => setNickname(suggestNickname())}
            className="text-xs font-medium text-tomato-600 hover:text-tomato-700 disabled:text-ink-300"
          >
            🎲 이름 추천받기
          </button>
        </div>
        {(localError || availability) && (
          <p className="mt-1.5 text-xs text-rose-600">{localError || availability}</p>
        )}
        <p className="mt-1.5 text-xs text-slate-400">
          나중에 바꿀 수 있지만 {NICKNAME_CHANGE_LIMIT.weeks}주에 {NICKNAME_CHANGE_LIMIT.max}번까지예요.
        </p>
      </div>

      <div className="mb-5">
        <Label hint="선택">계열사</Label>
        <Select
          value={affiliation}
          disabled={busy}
          onChange={(e) => setAffiliation(e.target.value as Affiliation | '')}
        >
          <option value="">선택 안 함</option>
          {AFFILIATIONS.map((code) => (
            <option key={code} value={code}>
              {AFFILIATION_LABELS[code]}
            </option>
          ))}
        </Select>
        <div className="mt-2.5">
          <Checkbox checked={visible} disabled={!affiliation || busy} onChange={setVisible}>
            다른 사람에게 계열사를 공개할래요
          </Checkbox>
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          공개하지 않으면 서버 응답에서 아예 빠집니다. 나중에 바꿀 수 있어요.
        </p>
      </div>

      <div className="mb-5 rounded-lg bg-slate-50 p-3.5">
        <Checkbox checked={agreed} disabled={busy} onChange={setAgreed}>
          <span className="font-medium text-slate-800">{me?.email}</span> 주소로{' '}
          <span className="font-medium text-slate-800">토마토 마켓</span>에 가입합니다.
          <span className="mt-1 block text-xs text-slate-500">
            이용약관·운영규칙 및 개인정보 처리방침(이메일·활동명·계열사 수집·이용)에 동의합니다.
          </span>
        </Checkbox>
      </div>

      <Button className="w-full" disabled={!canSubmit} onClick={() => void submit()}>
        {busy ? '가입 중…' : '시작하기'}
      </Button>
      <Notice>{error}</Notice>

      <div className="mt-4 text-center">
        <Button variant="ghost" className="px-0 py-0 text-xs" onClick={() => void logout()}>
          다른 계정으로 로그인
        </Button>
      </div>
    </CenterCard>
  );
}
